import { useState, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';

// ─────────────────────────────────────────────────────────────────────────────
// Configuración
// ─────────────────────────────────────────────────────────────────────────────

// Ruta a los archivos del modelo — deben existir en public/models/
// Archivos necesarios: tiny_face_detector_model-*.json + tiny_face_detector_model-*.bin
// Descargar de: https://github.com/justadudewhohacks/face-api.js/tree/master/weights
const MODEL_URL = `${process.env.PUBLIC_URL ?? ''}/models`;

// Umbral de confianza mínimo para considerar que hay un rostro detectado (0-1)
const DETECTION_SCORE_THRESHOLD = 0.5;

const TINY_FACE_OPTIONS = new faceapi.TinyFaceDetectorOptions({
  scoreThreshold: DETECTION_SCORE_THRESHOLD,
});

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface UseFaceDetectionReturn {
  // true cuando el modelo ya está cargado en memoria
  isModelLoaded: boolean;
  // true mientras el modelo se está descargando / cargando
  isModelLoading: boolean;
  // Detecta un rostro en el frame actual del elemento de video.
  // Retorna true si se detecta al menos un rostro, false en caso contrario.
  // Si el modelo no está cargado, retorna true (falla abierto) para no bloquear el flujo.
  detectFace: (videoEl: HTMLVideoElement) => Promise<boolean>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pre-validación de rostro usando face-api.js (modelo TinyFaceDetector).
 *
 * Propósito: verificar que el usuario tiene el rostro visible en cámara ANTES
 * de crear la sesión de AWS Rekognition, evitando desperdiciar sesiones de liveness
 * en encuadres vacíos o con iluminación insuficiente.
 *
 * Política de fallo abierto: si los modelos no cargan (red lenta, assets faltantes),
 * detectFace() retorna true para que el flujo continúe sin bloquear al usuario.
 *
 * Prerequisito de assets: los archivos del modelo deben estar en /public/models/:
 *   - tiny_face_detector_model-weights_manifest.json
 *   - tiny_face_detector_model-shard1  (archivo binario)
 */
export function useFaceDetection(): UseFaceDetectionReturn {
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(false);

  useEffect(() => {
    // Evitar carga duplicada si el modelo ya está en memoria de faceapi
    if (faceapi.nets.tinyFaceDetector.isLoaded) {
      setIsModelLoaded(true);
      return;
    }

    setIsModelLoading(true);

    faceapi.nets.tinyFaceDetector
      .loadFromUri(MODEL_URL)
      .then(() => {
        setIsModelLoaded(true);
      })
      .catch(() => {
        // Falla silenciosa — el pre-check es opcional y no debe bloquear el flujo
      })
      .finally(() => {
        setIsModelLoading(false);
      });
  }, []);

  const detectFace = useCallback(
    async (videoEl: HTMLVideoElement): Promise<boolean> => {
      // Falla abierto si el modelo no cargó o el video no tiene dimensiones válidas
      if (!isModelLoaded || videoEl.videoWidth === 0 || videoEl.videoHeight === 0) {
        return true;
      }

      try {
        const detection = await faceapi.detectSingleFace(videoEl, TINY_FACE_OPTIONS);
        return detection !== undefined;
      } catch {
        // Falla abierto en errores inesperados de WebGL o procesamiento de imagen
        return true;
      }
    },
    [isModelLoaded]
  );

  return { isModelLoaded, isModelLoading, detectFace };
}

export type { UseFaceDetectionReturn };
